import { createContext, useCallback, useContext, useMemo } from "react"
import { vec3 } from "../VectorMath.ts"
import { useUniformBlock } from "./useUniformBlock.ts"

export type PointLight = {
  position: vec3
  intensity: number
}

export type Lighting = {
  pointLights: [PointLight, PointLight, PointLight, PointLight]
  pointLightCoefficients: vec3
  ambientLight: number
}

export const defaultLighting: Lighting = (() => {
  const radius = 10
  const height = 10

  return {
    pointLights: [1 / 8, 3 / 8, 5 / 8, 7 / 8].map((turns, _i) => ({
      position: new vec3(radius * Math.cos(turns * 2 * Math.PI), height, radius * Math.sin(turns * 2 * Math.PI)),
      intensity: 0.40
    })) as [PointLight, PointLight, PointLight, PointLight],
    pointLightCoefficients: new vec3(1, 0, 0), // no falloff
    ambientLight: 0.4,
  }
})()

export const LightingContext = createContext<Lighting>(defaultLighting)

function dataForLighting(lighting: Lighting) {
  // This must match the layout of the Lighting block in lighting.glsl.
  let data = new Array<number>()
  for (const pointLight of lighting.pointLights) {
    data.push(...pointLight.position, pointLight.intensity)
  }
  data.push(...lighting.pointLightCoefficients)
  data.push(lighting.ambientLight)
  return new Float32Array(data)
}

export type RunLighting = (body: () => void) => void

export function useLighting(gl: WebGL2RenderingContext, program: WebGLProgram): RunLighting {
  const lighting = useContext(LightingContext)
  const data = useMemo(() => dataForLighting(lighting), [lighting])
  const block = useUniformBlock('Lighting', gl, program)
  return useCallback(body => {
    block.use(data, body)
  }, [block, data])
}
